import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ConditionalWrapper, useLockBodyScroll } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { Cookie, CookieConsentSettingState, } from '@as-react/common-types';
import { EventEnum } from '@as-react/common-types';
import { axiosClient } from '@as-react/common-utils';
import { cookieConsentSettingsParser, CookieUtil } from '@as-react/common-utils';
import { EventUtil } from '@as-react/common-utils';
import { ArrowLeftIcon, ArrowRightIcon } from '@yonderland/andes-icons';
import { Modal, Stack, ButtonAlpha, useIsClient, Breakpoint, BottomSheet, Box, Sidebar, useMediaQueryMatchesOnMobile, Icon, } from '@yonderland/andes-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { COOKIE_CONSENT_ACCEPTED } from '../../../constants/StorageKeysConstants';
import { analyticsActions } from '../../../redux/analytics/actions/AnalyticsActions.creators';
import cookieConsentActions from '../../../redux/cookieConsent/actions/CookieConsentActions.creators';
import { makeGetCookieConsentSettingAvailable, makeGetCookieConsentSettings, } from '../../../redux/cookieConsent/selectors/CookieConsentSelector';
import LocalStorageUtil from '../../../util/LocalStorageUtil';
import { HtmlInclude } from '../html-include/HtmlInclude';
import { CookieDefaultMessage } from './CookieDefaultMessage';
import { CookiePreferences } from './CookiePreferences';
export const FUNCTIONAL_KEY = 'functional';
export const COOKIE_TABS = {
    MESSAGE: 'message',
    PREFERENCES: 'preferences',
    PREFERENCE_DETAILS: 'preferenceDetails',
    PRIVACY: 'privacy',
};
const DIALOG_SELECTOR = '[data-testid="cookie-message-modal"]';
const CookieMessage = ({ panel1, panel2 }) => {
    const { t } = useTranslation(I18N_NAMESPACES.general);
    const { defaultRequestParams, servicesEndpoint } = useAemConfig();
    const cookieConsentSettings = useSelector(makeGetCookieConsentSettings());
    const cookieConsentSettingAvailable = useSelector(makeGetCookieConsentSettingAvailable());
    const dispatch = useDispatch();
    const isMobile = useMediaQueryMatchesOnMobile();
    const isClient = useIsClient();
    const [checkedPreferences, setCheckedPreferences] = useState({});
    const [initialCheckedPreferences, setInitialCheckedPreferences] = useState({});
    const shouldRenderCookieMessage = !LocalStorageUtil.getItem(COOKIE_CONSENT_ACCEPTED) &&
        cookieConsentSettingAvailable !== CookieConsentSettingState.CONSENTS_AVAILABLE &&
        !!panel1;
    const [open, setOpen] = useState(shouldRenderCookieMessage);
    const [privacyData, setPrivacyData] = useState('');
    const [preferenceData, setPreferenceData] = useState('');
    const [activeTab, setActiveTab] = useState(COOKIE_TABS.MESSAGE);
    const showFooter = activeTab !== COOKIE_TABS.PRIVACY;
    useLockBodyScroll(open);
    const convertCookieGroupsToObject = (data = cookieConsentSettings) => {
        if (panel2 === null || panel2 === void 0 ? void 0 : panel2.cookieGroups) {
            return panel2.cookieGroups.reduce((obj, item) => {
                obj[item.key] = data[item.key] || item.key === FUNCTIONAL_KEY;
                return obj;
            }, checkedPreferences);
        }
        return {};
    };
    useEffect(() => {
        if (!isClient) {
            return;
        }
        const cookieDialog = document.querySelector(DIALOG_SELECTOR);
        if (cookieDialog && !shouldRenderCookieMessage) {
            cookieDialog.close();
        }
    }, [isClient]);
    useEffect(() => {
        setCheckedPreferences(convertCookieGroupsToObject());
        setInitialCheckedPreferences(convertCookieGroupsToObject());
        const handleCookiekConsentEvent = () => {
            setOpen(true);
            const cookieConsentSettingsUpdated = cookieConsentSettingsParser(decodeURIComponent(CookieUtil.get(Cookie.CONSENT_SETTING)));
            setCheckedPreferences(convertCookieGroupsToObject(cookieConsentSettingsUpdated));
        };
        EventUtil.listen(EventEnum.COOKIE_CONSENT_PREFERENCES, handleCookiekConsentEvent);
        return () => {
            EventUtil.remove(EventEnum.COOKIE_CONSENT_PREFERENCES, handleCookiekConsentEvent);
        };
    }, []);
    if (!shouldRenderCookieMessage && !open) {
        return null;
    }
    const showCookiePreferences = (e) => {
        e.preventDefault();
        setActiveTab(COOKIE_TABS.PREFERENCES);
        dispatch(analyticsActions.cookieConsentOpenPreferences(checkedPreferences));
    };
    const showCookiePreference = (cookieGroup) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!panel2) {
            return;
        }
        const detailLink = (_a = panel2.cookieGroups.find((group) => group.key === cookieGroup.key)) === null || _a === void 0 ? void 0 : _a.detailLink;
        const axiosConfig = {
            method: 'GET',
            url: detailLink,
        };
        try {
            const res = yield axiosClient.request(axiosConfig);
            setPreferenceData(res.data);
        }
        catch (_b) { }
    });
    const saveCookiePreferences = () => __awaiter(void 0, void 0, void 0, function* () {
        setOpen(false);
        const axiosConfig = {
            method: 'POST',
            data: checkedPreferences,
            url: `${servicesEndpoint}api/eprivacy/consent`,
            params: defaultRequestParams,
            withCredentials: true,
        };
        try {
            yield axiosClient.request(axiosConfig);
            EventUtil.dispatch(EventEnum.COOKIE_CONSENT_PREFERENCES_UPDATED);
            LocalStorageUtil.setItem(COOKIE_CONSENT_ACCEPTED, true);
            dispatch(cookieConsentActions.createSetAction(checkedPreferences));
        }
        catch (_a) { }
    });
    const updateCookiePreferences = () => __awaiter(void 0, void 0, void 0, function* () {
        yield saveCookiePreferences();
        dispatch(analyticsActions.cookieConsentUpdated(checkedPreferences));
    });
    const changeCookiePreference = (e) => {
        const { value } = e.currentTarget;
        if (value === FUNCTIONAL_KEY) {
            return;
        }
        setCheckedPreferences((prevPreferences) => (Object.assign(Object.assign({}, prevPreferences), { [value]: !prevPreferences[value] })));
    };
    const handleDeclineAllCookies = () => __awaiter(void 0, void 0, void 0, function* () {
        const declinedPreferences = Object.keys(checkedPreferences).reduce((obj, item) => {
            obj[item] = item === FUNCTIONAL_KEY;
            return obj;
        }, checkedPreferences);
        setCheckedPreferences(declinedPreferences);
        yield saveCookiePreferences();
        dispatch(analyticsActions.cookieConsentDeclineAll(checkedPreferences));
    });
    const handleSaveAllCookies = () => __awaiter(void 0, void 0, void 0, function* () {
        setCheckedPreferences(Object.keys(checkedPreferences).reduce((obj, item) => {
            obj[item] = true;
            return obj;
        }, checkedPreferences));
        yield saveCookiePreferences();
        dispatch(analyticsActions.cookieConsentAcceptAll(checkedPreferences));
    });
    const handleBackClick = () => {
        switch (activeTab) {
            case COOKIE_TABS.PREFERENCES:
                setCheckedPreferences(initialCheckedPreferences);
                setActiveTab(COOKIE_TABS.MESSAGE);
                break;
            case COOKIE_TABS.PRIVACY:
                setActiveTab(COOKIE_TABS.MESSAGE);
                break;
            case COOKIE_TABS.PREFERENCE_DETAILS:
                dispatch(analyticsActions.cookieConsentOpenPreferences(checkedPreferences));
                setActiveTab(COOKIE_TABS.PREFERENCES);
                break;
        }
    };
    const getContent = () => {
        switch (activeTab) {
            case COOKIE_TABS.MESSAGE:
                return (_jsx(CookieDefaultMessage, Object.assign({}, panel1, { checkedPreferences: checkedPreferences, setActiveTab: setActiveTab, setPrivacyData: setPrivacyData })));
            case COOKIE_TABS.PREFERENCES:
                return (_jsx(CookiePreferences, Object.assign({}, panel2, { changeCookiePreference: changeCookiePreference, checkedPreferences: checkedPreferences, setActiveTab: setActiveTab, showCookiePreference: showCookiePreference })));
            case COOKIE_TABS.PREFERENCE_DETAILS:
                return (_jsx(Box, { testId: "cookie-modal-preference-details", children: _jsx(HtmlInclude, { htmlFragment: preferenceData }) }));
            case COOKIE_TABS.PRIVACY:
                return (_jsx(Box, { testId: "cookie-modal-privacy", children: _jsx(HtmlInclude, { htmlFragment: privacyData }) }));
        }
    };
    const getFooter = () => {
        switch (activeTab) {
            case COOKIE_TABS.MESSAGE:
                return (_jsxs(Stack, { gap: "2", wrap: "wrap", children: [_jsx(ButtonAlpha, { fullWidth: isMobile, theme: "branded", testId: "accept-all-cookies", onClick: handleSaveAllCookies, autoFocus: true, children: panel1.btnLabel }), _jsx(ButtonAlpha, { fullWidth: isMobile, theme: "branded", testId: "decline-all-cookies", onClick: handleDeclineAllCookies, children: panel1.declineBtnLabel }), _jsx(ButtonAlpha, { fullWidth: isMobile, theme: "branded", variant: "secondary", testId: "cookie-preferences-link", onClick: showCookiePreferences, children: panel1.moreOptions })] }));
            case COOKIE_TABS.PREFERENCES:
                return (_jsx(ConditionalWrapper, { condition: !isMobile, wrapper: children => _jsx(Stack, { justify: "end", children: children }), children: _jsxs(ButtonAlpha, { theme: "branded", testId: "save-cookie-preferences-button", onClick: updateCookiePreferences, children: [panel2.btnLabel, _jsx(Icon, { source: ArrowRightIcon, size: "sm" })] }) }));
            case COOKIE_TABS.PREFERENCE_DETAILS:
                return (_jsx(ConditionalWrapper, { condition: !isMobile, wrapper: children => _jsx(Stack, { children: children }), children: _jsxs(ButtonAlpha, { theme: "branded", variant: "secondary", testId: "back-cookie-preferences-button", onClick: handleBackClick, children: [_jsx(Icon, { source: ArrowLeftIcon, size: "sm" }), t('cookie.consent.back.button')] }) }));
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(Breakpoint, { until: "lg", children: _jsxs(BottomSheet, { testId: "cookie-message-bottom-sheet", isOpen: isClient ? open : true, defaultOpen: true, hasCloseBtn: false, disableClickOutside: true, fullHeightOnScroll: true, hasBackBtn: activeTab !== COOKIE_TABS.MESSAGE, onBack: handleBackClick, padding: "none", children: [_jsx(Box, { paddingBlockStart: "6", paddingBlockEnd: "20", children: getContent() }), showFooter && (_jsx(Sidebar, { isOpen: true, align: "bottom", position: "fixed", animation: "none", zIndex: "modal", hasCloseBtn: false, hasBackdrop: false, fullHeight: false, customWidth: "100%", children: getFooter() }))] }) }), _jsx(Breakpoint, { from: "lg", children: _jsxs(Modal, { testId: "cookie-message-modal", defaultOpen: true, isOpen: isClient ? open : undefined, hasBackBtn: activeTab !== COOKIE_TABS.MESSAGE, onBack: handleBackClick, hasCloseBtn: false, closedby: "none", children: [_jsx(Modal.Content, { children: getContent() }), showFooter && _jsx(Modal.Footer, { children: getFooter() })] }) })] }));
};
export default CookieMessage;
